import React, { useState } from "react";
import PropTypes from "prop-types";
import { useGuidance } from "./T2TGuidanceProvider";
import { useToast } from "../Layout/T2TUIProvider";

const T2THelpPanel = ({ open, onClose, onOpenGallery, onOpenWizard, onOpenTutorial }) => {
  const { getProgress, refreshProgress, resetHints, hintsEnabled, setHintsEnabled, state, resetWizardProgress, resetTutorialProgress } = useGuidance();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState("menu"); // menu | progress
  const progress = getProgress();

  if (!open) return null;

  return (
    <div className="t2t-help-overlay" onClick={onClose}>
      <div className="t2t-help-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="t2t-help-header">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>?</span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>Help &amp; Guidance</span>
          </div>
          <button className="t2t-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tab toggle */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--t2t-border)", padding: "0 16px" }}>
          <button
            onClick={() => setActiveSection("menu")}
            style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, border: "none", background: "none", cursor: "pointer", borderBottom: activeSection === "menu" ? "2px solid var(--t2t-primary)" : "2px solid transparent", color: activeSection === "menu" ? "var(--t2t-primary)" : "var(--t2t-text-secondary)" }}
          >Quick Actions</button>
          <button
            onClick={() => setActiveSection("progress")}
            style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, border: "none", background: "none", cursor: "pointer", borderBottom: activeSection === "progress" ? "2px solid var(--t2t-primary)" : "2px solid transparent", color: activeSection === "progress" ? "var(--t2t-primary)" : "var(--t2t-text-secondary)" }}
          >Learning Progress</button>
        </div>

        {/* Body */}
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {activeSection === "menu" && (
            <>
              {/* Guidance Actions */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Get Started</div>
                <div className="t2t-help-menu">
                  <HelpMenuItem
                    icon="🧭"
                    title="Guided Wizard"
                    desc="Step-by-step wizard to build your first workflow"
                    tag={state.wizard_completed ? "Completed" : state.wizard_started ? "Resume" : null}
                    onClick={() => { onClose(); if (onOpenWizard) onOpenWizard(); }}
                  />
                  <HelpMenuItem
                    icon="🎓"
                    title="Interactive Tutorial"
                    desc="Learn by building a sample Expense Approval workflow"
                    tag={state.tutorial_completed ? "Completed" : state.tutorial_started ? "Resume" : null}
                    onClick={() => { onClose(); if (onOpenTutorial) onOpenTutorial(); }}
                  />
                  <HelpMenuItem
                    icon="📋"
                    title="Browse Templates"
                    desc="Start from a pre-built workflow and customise it"
                    onClick={() => { onClose(); if (onOpenGallery) onOpenGallery(); }}
                  />
                </div>
              </div>

              {/* Settings */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Settings</div>
                <div className="t2t-help-menu">
                  <div className="t2t-help-menu-item" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 16, width: 28, textAlign: "center" }}>💡</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Contextual Hints</div>
                        <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>Show helpful tips on first interactions</div>
                      </div>
                    </div>
                    <label style={{ position: "relative", width: 36, height: 20, flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={hintsEnabled}
                        onChange={(e) => setHintsEnabled(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: "absolute", inset: 0, borderRadius: 20, cursor: "pointer", transition: "0.2s",
                        background: hintsEnabled ? "var(--t2t-primary)" : "#CBD5E1",
                      }}>
                        <span style={{
                          position: "absolute", left: hintsEnabled ? 18 : 2, top: 2, width: 16, height: 16,
                          borderRadius: "50%", background: "white", transition: "0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)"
                        }} />
                      </span>
                    </label>
                  </div>
                  <HelpMenuItem
                    icon="↺"
                    title="Reset All Hints"
                    desc="Show all contextual hints again"
                    onClick={() => {
                      // BUG-45: existing reset call already works; add user-visible feedback
                      resetHints();
                      toast("Hints restored — they'll re-appear as you interact with the builder.", "success");
                    }}
                  />
                  {/* BUG-45: explicit replay affordances for tutorial + wizard. The Quick
                      Actions buttons above already restart from step 1; these menu items
                      duplicate that intent in the Settings area where users look for
                      "reset"-style actions. The Completed badge stays as a truthful record. */}
                  <HelpMenuItem
                    icon="🎓"
                    title="Replay Tutorial"
                    desc="Restart the interactive tutorial from step 1"
                    onClick={async () => {
                      // BUG-76: true replay — reset progress before opening
                      if (resetTutorialProgress) await resetTutorialProgress();
                      onClose();
                      if (onOpenTutorial) onOpenTutorial();
                    }}
                  />
                  <HelpMenuItem
                    icon="🧭"
                    title="Replay Wizard"
                    desc="Restart the guided wizard from the beginning"
                    onClick={async () => {
                      if (resetWizardProgress) await resetWizardProgress();
                      onClose();
                      if (onOpenWizard) onOpenWizard();
                    }}
                  />
                </div>
              </div>

              {/* External Links */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Resources</div>
                <div className="t2t-help-menu">
                  <HelpMenuItem
                    icon="📖"
                    title="Documentation"
                    desc="Detailed reference for all builder features"
                    onClick={() => {
                      // BUG-76: honest feedback until external docs URL is wired
                      toast("Documentation site coming soon. Use the page-help (?) buttons next to each tab for now.", "info");
                    }}
                  />
                  <HelpMenuItem
                    icon="💬"
                    title="Contact Support"
                    desc="Get help from the PBOPlus team"
                    onClick={() => {
                      // BUG-76: open mailto to the PBOPlus support address (matches footer branding)
                      if (typeof window !== "undefined") {
                        window.location.href = "mailto:support@pboplus.org?subject=OSIF%20T2T%20Support%20Request";
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {activeSection === "progress" && (
            <>
              {/* Progress bar */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>Your Learning Progress</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--t2t-primary)" }}>{progress.percent}%</span>
                </div>
                <div style={{ height: 8, background: "#E2E8F0", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${progress.percent}%`, background: "var(--t2t-primary)", borderRadius: 8, transition: "width 0.5s ease" }} />
                </div>
                <button onClick={refreshProgress} style={{ marginTop: 6, background: "none", border: "none", fontSize: 10, color: "var(--t2t-primary)", cursor: "pointer", fontWeight: 600 }}>↻ Refresh</button>
              </div>

              {/* Milestones */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {progress.milestones.map((m) => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: m.done ? "#F0FDF4" : "#F8FAFC", borderRadius: 8, border: `1px solid ${m.done ? "#BBF7D0" : "#E2E8F0"}` }}>
                    <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{m.done ? "✅" : "○"}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: m.done ? 600 : 400, color: m.done ? "#166534" : "var(--t2t-text-secondary)" }}>{m.label}</span>
                    {!m.done && m.action && (
                      <button
                        onClick={() => {
                          onClose();
                          if (m.action === "wizard" && onOpenWizard) onOpenWizard();
                          if (m.action === "tutorial" && onOpenTutorial) onOpenTutorial();
                          if (m.action === "templates" && onOpenGallery) onOpenGallery();
                        }}
                        style={{ fontSize: 10, fontWeight: 600, color: "var(--t2t-primary)", background: "none", border: "1px solid var(--t2t-primary)", borderRadius: 4, padding: "2px 8px", cursor: "pointer", whiteSpace: "nowrap" }}
                      >Start →</button>
                    )}
                  </div>
                ))}
              </div>

              {/* Completion message */}
              {progress.percent === 100 && (
                <div style={{ marginTop: 16, padding: 14, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>🏆</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#166534" }}>All milestones complete!</div>
                  <div style={{ fontSize: 12, color: "#166534", marginTop: 2 }}>You&apos;re a Workflow Builder expert.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Menu Item sub-component ----
const HelpMenuItem = ({ icon, title, desc, tag, onClick }) => (
  <div className="t2t-help-menu-item" onClick={onClick}>
    <span style={{ fontSize: 16, width: 28, textAlign: "center", flexShrink: 0 }}>{icon}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
        {title}
        {tag && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 20, background: tag === "Completed" ? "#F0FDF4" : "#FFF7ED", color: tag === "Completed" ? "#166534" : "#9A3412", border: `1px solid ${tag === "Completed" ? "#BBF7D0" : "#FED7AA"}` }}>{tag}</span>}
      </div>
      <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 1 }}>{desc}</div>
    </div>
    <span style={{ fontSize: 14, color: "#CBD5E1", flexShrink: 0 }}>›</span>
  </div>
);

T2THelpPanel.propTypes = { open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onOpenGallery: PropTypes.func, onOpenWizard: PropTypes.func, onOpenTutorial: PropTypes.func };
HelpMenuItem.propTypes = { icon: PropTypes.string, title: PropTypes.string, desc: PropTypes.string, tag: PropTypes.string, onClick: PropTypes.func };

export default T2THelpPanel;
